import { IconMinus, IconPlus } from '@tabler/icons-react';
import { Mod } from '@components/arsenal/mods/Mod';
import { useAppStore, UserData } from '@stores';
import { useDissolveModsMutation } from '@http';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button';
import { Modal } from '@components/ui/Modal';
import * as Shared from '@shared/lib';
import { useState } from 'react';

interface ModDissolveProps {
  mod: UserData['mods'][0];
  open: boolean;
  onClose: () => void;
};

export function ModDissolve({ mod, open, onClose }: ModDissolveProps) {
  const [amount, setAmount] = useState(1);

  const { t } = useTranslation(undefined, { keyPrefix: 'Mods' });
  const { mutateAsync } = useDissolveModsMutation();
  const { userData, isFetching, setIsFetching, refetch } = useAppStore();

  const sameMods = userData.mods
    .filter(m => m.key === mod.key && m.level === mod.level && m.rank === mod.rank)
    .filter(m => !userData.weapons.some(w => w.mods.some(wm => wm.modId === m.id)))
    .filter(m => !userData.chassis.some(c => c.mods.some(cm => cm.modId === m.id)));

  const inc = () => setAmount(Math.min(amount + 1, sameMods.length));
  const dec = () => setAmount(Math.max(amount - 1, 1));
  const refund = Shared.DissolveModCalculator.calc(mod.level, mod.rank) * amount;

  const dissolve = async () => {
    if (isFetching) return;
    setIsFetching(true);

    await mutateAsync({ id: mod.id, amount });
    refetch();
    onClose();
  };

  return (
    <Modal title={t('dissolveModal')} onRequestClose={onClose} isOpen={open}>
      <div className="flex flex-col items-center p-4">
        <Mod previewLevel={mod.level} mod={mod} />

        <div className="flex w-full items-center justify-between mt-4">
          <button className="mod-decrease-level" onClick={dec}>
            <IconMinus />
          </button>

          <p className="mod-level-text">
            {amount}/{sameMods.length}
          </p>

          <button className="mod-increase-level" onClick={inc}>
            <IconPlus />
          </button>
        </div>

        <Button onClick={dissolve} className="mt-4">
          <div className="w-full flex items-center justify-center">
            <p>{t('dissolveFor')}</p>
            <p className="text-primary ml-1.5">{refund}</p>
            <img src="/assets/items/resonite.avif" className="w-3 h-3 ml-1" />
          </div>
        </Button>
      </div>
    </Modal>
  );
}